import SceneManager from "@managers/scene";
import PhysicsManager from "@managers/physics";
import LightManager from "@managers/lights/LightManager";
import RendererManager from "@managers/renderer/RendererManager";
import EntityManager from "@managers/entities/EntityManager";
import PerformanceCounter from "./performance/PerformanceCounter";
import { running, setRunning, playerState, setPlayerState, setFPS, setPPS, setLPS } from "@stores";
import TerrainManager from "@managers/terrain";
import AssetLoader from "@managers/loaders";
import { Clock } from "three";
import CameraManager from "@managers/camera";
import { UIInputController } from "@controllers/input";
class GameObject {
    assetLoader;
    physicsClock;
    logicClock;
    sceneManager;
    rendererManager;
    physicsManager;
    entityManager;
    terrainManager;
    cameraManager;
    lightsManager;
    uiInput;
    fpsCounter;
    ppsCounter;
    lpsCounter;
    logicInterval;
    overlayInterval;
    static instance;
    /**
     * The Game object.
     * @private
     */
    constructor() {
        this.physicsClock = new Clock();
        this.logicClock = new Clock();
        this.assetLoader = AssetLoader.getInstance();
        this.sceneManager = SceneManager.getInstance();
        this.rendererManager = RendererManager.getInstance();
        this.physicsManager = PhysicsManager.getInstance();
        this.terrainManager = TerrainManager.getInstance(this.sceneManager.getScene, this.physicsManager.getWorld, this.assetLoader);
        this.entityManager = EntityManager.getInstance(this.sceneManager.getScene, this.physicsManager.getWorld);
        this.cameraManager = CameraManager.getInstance(this.entityManager.getPlayer.getSkeleton.eyes.getBone);
        this.lightsManager = LightManager.getInstance(this.sceneManager.getScene);
        this.fpsCounter = new PerformanceCounter(setFPS);
        this.ppsCounter = new PerformanceCounter(setPPS);
        this.lpsCounter = new PerformanceCounter(setLPS);
        this.logicInterval = 60;
        this.overlayInterval = 5;
        setPlayerState({
            ...playerState(),
            position: this.entityManager.getPlayer.getPosition,
            health: this.entityManager.getPlayer.getHealth,
            gun: {
                name: this.entityManager.getPlayer.getGun.getName,
                imageSource: this.entityManager.getPlayer.getGun.getImageSource,
                firingMode: this.entityManager.getPlayer.getGun.getFiringModeManager.getFiringMode
                    .getShortName,
                allowedFiringModes: this.entityManager.getPlayer.getGun.getFiringModeManager.getAllowedFiringModes.map(allowedFiringMode => allowedFiringMode.getShortName),
            },
        });
        this.uiInput = UIInputController.getInstance(this.cameraManager, this.rendererManager);
    }
    /**
     * Start the game.
     */
    run() {
        setRunning(true);
        this.stepPhysics();
        this.stepLogic();
        this.stepRender();
        this.stepOverlay();
    }
    /**
     * Re-render the scene.
     * Also updates the fps counter.
     * @private
     */
    stepRender() {
        if (!running())
            return;
        requestAnimationFrame(() => {
            this.rendererManager.step(this.sceneManager.getScene, this.cameraManager.getSelected);
            this.fpsCounter.step();
            this.stepRender();
        });
    }
    /**
     * Update the physics world.
     * @private
     */
    stepPhysics() {
        if (!running())
            return;
        requestAnimationFrame(() => {
            const delta = this.physicsClock.getDelta();
            this.physicsManager.getWorld.stepSimulation(delta, 1);
            this.ppsCounter.step();
            this.stepPhysics();
        });
    }
    /**
     * Update the logic for all entities.
     * @private
     */
    stepLogic() {
        if (!running())
            return;
        setTimeout(() => {
            const delta = this.logicClock.getDelta();
            this.entityManager.step(this.sceneManager.getScene, this.physicsManager.getWorld, this.terrainManager.getGround, delta);
            this.lpsCounter.step();
            this.stepLogic();
        }, 1000 / this.logicInterval);
    }
    /**
     * Update values visible on the debug overlay.
     * @private
     */
    stepOverlay() {
        setTimeout(() => {
            setPlayerState({
                ...playerState(),
                position: this.entityManager.getPlayer.getPosition,
                health: this.entityManager.getPlayer.getHealth,
            });
            this.stepOverlay();
        }, 1000 / this.overlayInterval);
    }
    static getInstance() {
        if (!GameObject.instance)
            GameObject.instance = new GameObject();
        return GameObject.instance;
    }
}
export default GameObject;
